import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Upload, CheckCircle, XCircle, AlertTriangle, ChevronRight, ChevronLeft, ChevronDown,
  RefreshCw, Users, User, ArrowRight, Loader2, Database, Clock, Check, Building2, Search,
  ListChecks, Link2, FileSpreadsheet, Sparkles, ShieldCheck, Info, Wand2, Download,
  Paperclip, RotateCcw, History,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE, SectionCard, DataTable, KpiTile, ErrorState, ProgressBar, StatusPill,
  fmtDateTime, humanise, num, plural,
} from '../../components/ui';

// Data import (/migration): bring an organisation's records across from other software. Upload any
// CSV or Excel export → presets and AI map its columns → dry run → import, with a per-batch Undo in
// the history. Org admins can only import into their own organisation, so the page opens straight
// on the checklist; super admins choose the destination first. The top bar already titles the page
// ("Data import"), so there is no heading here.

// The source platform is never chosen by the admin (AI maps any spreadsheet), so parse, analyse and
// execute always send 'generic'.
const SOURCE_PLATFORM = 'generic';

// The backend names data types and fields in title case ('Care Plans'). Pages use sentence case, so
// labels are shortened for display: 'Care Plans' → 'Care plans'. A word with capitals after its
// first letter (eMAR, NDIS, URL) is a name and stays as written.
function sentenceCase(s) {
  let first = true;
  return String(s || '').replace(/[A-Za-z]+/g, (w) => {
    if (first) { first = false; return w; }
    return /^[A-Z][a-z]+$/.test(w) ? w.toLowerCase() : w;
  });
}
// The same label inside a sentence: 'Drop the clients (participants) export here'.
const inlineLabel = (s) => sentenceCase(s).replace(/^[A-Z][a-z]+/, (w) => w.toLowerCase());

// What each record type an import creates is called (migration_audit.entity), so the Undo warning
// can say what will go: '120 clients, 94 staff'. Listed in import order.
const RECORD_NOUNS = {
  clients:               ['client', 'clients'],
  staff:                 ['staff member', 'staff'],
  client_contacts:       ['client contact', 'client contacts'],
  client_funds:          ['funding record', 'funding records'],
  care_plans:            ['care plan', 'care plans'],
  medications:           ['medication', 'medications'],
  client_goals:          ['client goal', 'client goals'],
  client_documents:      ['client document', 'client documents'],
  service_agreements:    ['service agreement', 'service agreements'],
  restrictive_practices: ['restrictive practice', 'restrictive practices'],
  staff_documents:       ['staff document', 'staff documents'],
  staff_qualifications:  ['staff qualification', 'staff qualifications'],
  staff_training:        ['training record', 'training records'],
  leave_balances:        ['leave balance', 'leave balances'],
  shifts:                ['shift', 'shifts'],
  shift_clients:         ['shift client link', 'shift client links'],
  progress_notes:        ['progress note', 'progress notes'],
  incidents:             ['incident', 'incidents'],
  other:                 ['other record', 'other records'],
};

// { clients: 120, staff: 94 } → [{ key, n: 120, text: '120 clients' }, { key, n: 94, text: '94 staff' }]
function recordBreakdown(counts) {
  if (!counts || typeof counts !== 'object') return [];
  const keys = Object.keys(counts).filter((k) => Number(counts[k]) > 0);
  const known = Object.keys(RECORD_NOUNS).filter((k) => keys.includes(k));
  const unknown = keys.filter((k) => !RECORD_NOUNS[k]).sort();
  return [...known, ...unknown].map((k) => {
    const n = Number(counts[k]) || 0;
    const [one, many] = RECORD_NOUNS[k] || [humanise(k).toLowerCase()];
    return { key: k, n, text: plural(n, one, many) };
  });
}

// ['120 clients', '94 staff', '12 shifts'] → '120 clients, 94 staff and 12 shifts'
const joinList = (parts) => (parts.length < 2 ? parts.join('')
  : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`);

// History: where a batch came from and how it ended, in the app's words.
const SOURCE_LABELS = { generic: 'Spreadsheet', full_transfer: 'Workbook', documents_zip: 'ZIP of documents' };
const sourceLabel = (v) => SOURCE_LABELS[v] || humanise(v) || '—';
const HISTORY_STATUS = {
  completed: { label: 'Completed', tone: 'success' },
  partial:   { label: 'Partial', tone: 'warning' },
  failed:    { label: 'Failed', tone: 'danger' },
};

// A link field points at an existing client or staff member.
const FK_NOUN = { client: 'client', staff: 'staff member' };

// ── Small presentational pieces ──────────────────────────────────────────────

// The promise behind the whole page, as one quiet line.
function AiMappingNote() {
  return (
    <p className="flex items-start gap-2 text-sm leading-relaxed text-slate-500">
      <Wand2 size={15} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
      <span>
        No need to pick your old software. Upload any CSV or Excel export and AI reads the column
        headers and sample values to match them to Rostery fields. You review every match before
        anything is imported.
      </span>
    </p>
  );
}

// A file chooser that is a real button (the hidden <input type="file"> does the picking).
function FilePickButton({ file, icon: Icon, disabled, onPick, emptyTitle, emptyHint, changeHint }) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      className={clsx(
        'flex min-w-0 flex-1 items-center gap-3 rounded-xl border border-dashed px-4 py-3 text-left',
        'transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60',
        file ? 'border-slate-300 bg-slate-50' : 'border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50',
      )}
    >
      <Icon size={16} className={clsx('shrink-0', file ? 'text-emerald-600' : 'text-slate-400')} aria-hidden="true" />
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-slate-900">{file ? file.name : emptyTitle}</span>
        <span className="block text-xs text-slate-500">{file ? changeHint : emptyHint}</span>
      </span>
    </button>
  );
}

// An import outcome as the kit's number tiles. A tile turns rose only when rows failed.
function ResultTiles({
  imported, skipped, unresolved, failed, showUnlinked,
  importedLabel = 'Imported', skippedLabel = 'Skipped', unlinkedLabel = 'Unlinked', failedLabel = 'Failed',
}) {
  return (
    <div className={clsx('grid grid-cols-2 gap-3', showUnlinked ? 'sm:grid-cols-4' : 'sm:grid-cols-3')}>
      <KpiTile label={importedLabel} value={num(imported)} accent="emerald" />
      <KpiTile label={skippedLabel} value={num(skipped)} accent="slate" />
      {showUnlinked && (
        <KpiTile label={unlinkedLabel} value={num(unresolved)} accent={unresolved > 0 ? 'amber' : 'slate'} />
      )}
      <KpiTile label={failedLabel} value={num(failed)} accent="slate" alert={failed > 0} />
    </div>
  );
}

// Row-level problems in one bordered, scrollable list.
function IssueList({ title, items, icon: Icon = XCircle, maxHeight = 'max-h-48' }) {
  if (!items.length) return null;
  return (
    <div>
      <p className="mb-1.5 text-sm font-medium text-slate-700">{title}</p>
      <ul className={clsx('divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200', maxHeight)}>
        {items.map((it) => (
          <li key={it.key} className="flex items-start gap-2 px-3 py-2 text-[13px] text-slate-700">
            <Icon size={14} className="mt-0.5 shrink-0 text-red-500" aria-hidden="true" />
            <span className="min-w-0 break-words">{it.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── AI Analysis Panel ─────────────────────────────────────────────────────────
function AiAnalysisPanel({ platform, entityType, entityLabel, parsedData, mappings }) {
  const [analysis, setAnalysis]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [ran, setRan]             = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/migration/ai-analyse', {
        entityType,
        platform,
        columns:   parsedData.columns,
        preview:   parsedData.preview,
        totalRows: parsedData.totalRows,
        mappings:  mappings || null,
      });
      setAnalysis(data);
      setRan(true);
    } catch {
      toast.error('AI analysis unavailable');
    } finally {
      setLoading(false);
    }
  };

  // Colour here is state only: how ready the file is, and how serious each finding is.
  const scoreText = (s) => (s >= 80 ? 'text-emerald-700' : s >= 60 ? 'text-amber-700' : 'text-red-700');
  const scoreBadge = (s) => (s >= 80 ? 'badge-green' : s >= 60 ? 'badge-yellow' : 'badge-red');
  const severityIcon = (sv) => {
    if (sv === 'error')   return <><XCircle size={14} className="mt-0.5 shrink-0 text-red-500" aria-hidden="true" /><span className="sr-only">Error: </span></>;
    if (sv === 'warning') return <><AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-500" aria-hidden="true" /><span className="sr-only">Warning: </span></>;
    return <><Info size={14} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" /><span className="sr-only">Note: </span></>;
  };
  const qualityDot = (st) => (st === 'good' ? 'bg-emerald-500' : st === 'warning' ? 'bg-amber-400' : 'bg-red-500');
  const score = analysis?.readinessScore;

  return (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">AI data analysis</p>
          <p className="mt-0.5 text-sm text-slate-500">AI reviews your file for quality and NDIS compliance.</p>
        </div>
        {!ran ? (
          <button type="button" onClick={run} disabled={loading} className="btn-secondary btn-sm">
            {loading ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Sparkles size={13} aria-hidden="true" />}
            {loading ? 'Analysing…' : 'Analyse data'}
          </button>
        ) : (
          <button type="button" onClick={run} disabled={loading} className="btn-ghost btn-sm">
            {loading ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <RefreshCw size={13} aria-hidden="true" />}
            {loading ? 'Analysing again…' : 'Analyse again'}
          </button>
        )}
      </div>

      {!ran && !loading && (
        <p className="text-sm text-slate-500">
          Optional: get a readiness score, the issues it finds and NDIS compliance recommendations before
          you import.
        </p>
      )}

      {loading && (
        <p className="flex items-center gap-2 text-sm text-slate-500" role="status">
          <Loader2 size={14} className="animate-spin text-slate-400" aria-hidden="true" />
          AI is reviewing your {entityLabel} data…
        </p>
      )}

      {analysis && !loading && (
        <div className="space-y-4">
          {/* Score + summary */}
          <div className="flex items-start gap-4">
            <div className="w-20 shrink-0 rounded-lg border border-slate-200 py-2.5 text-center">
              <div className={clsx('text-2xl font-semibold leading-none tabular-nums', scoreText(score))}>{score}</div>
              <div className="mt-1 text-xs text-slate-500">of 100</div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                {analysis.readinessLabel && (
                  <span className={scoreBadge(score)}>{sentenceCase(analysis.readinessLabel)}</span>
                )}
                {parsedData?.totalRows != null && (
                  <span className="text-xs text-slate-500">{plural(parsedData.totalRows, 'record')}</span>
                )}
              </div>
              {analysis.summary && <p className="text-sm leading-relaxed text-slate-600">{analysis.summary}</p>}
            </div>
          </div>

          {/* Field quality */}
          {analysis.dataQuality?.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-slate-700">Field quality</p>
              <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {analysis.dataQuality.map((q, i) => (
                  <li key={i} className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5" title={q.note || undefined}>
                    <span className={clsx('h-2 w-2 shrink-0 rounded-full', qualityDot(q.status))} aria-hidden="true" />
                    <span className="sr-only">{humanise(q.status)}: </span>
                    <span className="truncate text-xs font-semibold text-slate-700">{q.field}</span>
                    <span className="flex-1 truncate text-xs text-slate-500">{q.note}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Issues */}
          {analysis.issues?.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-slate-700">Issues found</p>
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {analysis.issues.map((iss, i) => (
                  <li key={i} className="flex items-start gap-2 px-3 py-2 text-[13px] text-slate-700">
                    {severityIcon(iss.severity)}
                    <span className="min-w-0">{iss.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {analysis.recommendations?.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-slate-700">Recommendations</p>
              <ul className="space-y-1.5">
                {analysis.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-slate-600">
                    <CheckCircle size={13} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
                    <span className="min-w-0">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* NDIS compliance note */}
          {analysis.ndisCompliance && (
            <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              <ShieldCheck size={14} className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />
              <p className="text-[13px] text-slate-700">{analysis.ndisCompliance}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Icon per entity group order — keeps the checklist readable at a glance.
const ORDER_ICON = {
  1: Users,
  2: User,
  3: Link2,
  4: Clock,
  5: FileSpreadsheet,
};
// Short human label for each "order" group (the spine of the import flow).
const ORDER_NOTE = {
  1: 'Participants — the foundation everything else links to.',
  2: 'Support workers — needed before shifts and notes can link to them.',
  3: 'Funding, contacts, plans, documents and training — each links to a client or staff member.',
  4: 'Roster shifts — link to a client and, optionally, a staff member.',
  5: 'Progress notes and incidents — link to a client and a staff member.',
};

// ── Step indicator for the per-entity mini-flow ───────────────────────────────
const ENTITY_STEPS = [
  { label: 'Upload',     num: 1 },
  { label: 'Map fields', num: 2 },
  { label: 'Validate',   num: 3 },
  { label: 'Importing',  num: 4 },
  { label: 'Done',       num: 5 },
];

function StepBar({ current }) {
  return (
    <ol className="mb-6 flex items-start" aria-label="Import steps">
      {ENTITY_STEPS.map((s, i) => {
        const done = current > s.num;
        const on = current === s.num;
        const last = i === ENTITY_STEPS.length - 1;
        return (
          <li key={s.num} className={clsx('flex items-start', !last && 'flex-1')} aria-current={on ? 'step' : undefined}>
            <div className="flex shrink-0 flex-col items-center">
              <span className={clsx(
                'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold tabular-nums transition-colors duration-150',
                done ? 'bg-primary-600 text-white'
                  : on ? 'bg-primary-600 text-white ring-4 ring-primary-100'
                    : 'bg-slate-100 text-slate-500',
              )}>
                {done ? <Check size={13} aria-hidden="true" /> : s.num}
              </span>
              {/* Phones show only the current step's name; wider screens show them all. */}
              <span className={clsx(
                'mt-1.5 whitespace-nowrap text-xs',
                on ? 'font-semibold text-slate-900' : 'hidden text-slate-500 sm:block',
              )}>
                {s.label}{done && <span className="sr-only"> (done)</span>}
              </span>
            </div>
            {!last && (
              <span aria-hidden="true" className={clsx('mx-2 mt-3.5 h-0.5 flex-1 rounded-full', done ? 'bg-primary-600' : 'bg-slate-200')} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ── Target-organisation picker (searchable, super admin only) ─────────────────
// The list opens in the page flow rather than floating, so the card around it can never clip it.
function OrgPicker({ orgs, loading, value, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    function onDoc(e) { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const selected = orgs.find(o => o.id === value);
  const filtered = orgs.filter(o => {
    const q = search.toLowerCase();
    return !q || (o.name || '').toLowerCase().includes(q) || (o.email || '').toLowerCase().includes(q);
  });

  return (
    <div ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={clsx(
          'flex w-full items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3 text-left transition-colors duration-150',
          open ? 'border-primary-400 ring-2 ring-primary-100' : 'border-slate-300 hover:border-slate-400',
        )}
      >
        <span className="flex min-w-0 items-center gap-3">
          <Building2 size={18} className="shrink-0 text-slate-400" aria-hidden="true" />
          <span className="min-w-0">
            {selected ? (
              <>
                <span className="block truncate text-sm font-semibold text-slate-900">{selected.name}</span>
                {selected.email && <span className="block truncate text-xs text-slate-500">{selected.email}</span>}
              </>
            ) : (
              <>
                <span className="block text-sm font-semibold text-slate-700">
                  {loading ? 'Loading organisations…' : 'Select an organisation to import into'}
                </span>
                <span className="block text-xs text-slate-500">Only approved organisations are shown</span>
              </>
            )}
          </span>
        </span>
        <ChevronDown size={16} className={clsx('shrink-0 text-slate-400 transition-transform duration-150', open && 'rotate-180')} aria-hidden="true" />
      </button>

      {open && (
        <div className="mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-2">
            <label className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
              <Search size={14} className="shrink-0 text-slate-400" aria-hidden="true" />
              <span className="sr-only">Search organisations</span>
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or email…"
                className="w-full bg-transparent text-sm text-slate-700 focus:outline-none"
              />
            </label>
          </div>
          <div className="max-h-64 overflow-y-auto" role="listbox" aria-label="Organisations">
            {loading ? (
              <div className="space-y-2 p-3" aria-busy="true">
                {[0, 1, 2].map(i => <div key={i} className="skeleton h-9" />)}
              </div>
            ) : filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">No approved organisations found</p>
            ) : (
              filtered.map(o => (
                <button
                  key={o.id}
                  type="button"
                  role="option"
                  aria-selected={o.id === value}
                  onClick={() => { onChange(o); setOpen(false); setSearch(''); }}
                  className={clsx(
                    'flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left transition-colors duration-150',
                    o.id === value ? 'bg-primary-50' : 'hover:bg-slate-50',
                  )}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-slate-800">{o.name}</span>
                    {o.email && <span className="block truncate text-xs text-slate-500">{o.email}</span>}
                  </span>
                  {o.id === value && <Check size={15} className="shrink-0 text-primary-600" aria-hidden="true" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Result of a one-workbook import: totals, a row per sheet, then anything that went wrong ──
function FullTransferReport({ result }) {
  const t = result.totals || {};
  const sheets = result.sheets || [];
  const errors = sheets.flatMap(s => (s.errors || []).map((e, j) => ({
    key: `${s.entity}-err-${j}`,
    text: <><span className="font-medium">{sentenceCase(s.label)}</span>{e.row ? ` row ${e.row}` : ''}: {e.message}</>,
  })));
  const unknown = result.unknownSheets || [];
  return (
    <div className="space-y-4 border-t border-slate-100 pt-4">
      <ResultTiles imported={t.imported} skipped={t.skipped} unresolved={t.unresolved} failed={t.failed} showUnlinked />

      <div className="overflow-hidden rounded-xl border border-slate-200">
        <DataTable
          label="Results by sheet"
          dense
          stickyHeader={false}
          rows={sheets}
          rowKey={(s, i) => `${s.entity}-${i}`}
          empty={{ icon: FileSpreadsheet, title: 'No sheets were imported', hint: "None of the workbook's sheet names matched a data type." }}
          columns={[
            {
              key: 'label', header: 'Data type',
              render: (s) => (
                <span className="text-slate-900">
                  {sentenceCase(s.label)}
                  {s.detected && <span className="ml-1.5 text-xs text-slate-500">· {s.detected}</span>}
                </span>
              ),
            },
            { key: 'rows', header: 'Rows', align: 'right', className: 'tabular-nums', render: (s) => num(s.rows) },
            {
              key: 'imported', header: 'Imported', align: 'right', className: 'tabular-nums',
              render: (s) => <span className="font-semibold text-slate-900">{num(s.imported)}</span>,
            },
            { key: 'skipped', header: 'Skipped', align: 'right', className: 'tabular-nums', render: (s) => num(s.skipped) },
            {
              key: 'unresolved', header: 'Unlinked', align: 'right', className: 'tabular-nums',
              render: (s) => <span className={s.unresolved > 0 ? 'font-semibold text-amber-700' : undefined}>{num(s.unresolved)}</span>,
            },
            {
              key: 'failed', header: 'Failed', align: 'right', className: 'tabular-nums',
              render: (s) => <span className={s.failed > 0 ? 'font-semibold text-red-600' : undefined}>{num(s.failed)}</span>,
            },
          ]}
        />
      </div>

      {unknown.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
          <p className="min-w-0 break-words text-sm leading-relaxed text-amber-900">
            <strong className="font-semibold">{plural(unknown.length, 'sheet')} skipped</strong> because the name
            didn't match a data type: {unknown.join(', ')}. Rename them to match the template's sheet names to
            include them.
          </p>
        </div>
      )}

      <IssueList title="Issues during import" items={errors} maxHeight="max-h-40" />
    </div>
  );
}

// ── Result of a document ZIP import ──
function DocumentReport({ result }) {
  const skipped = result.skipped || [];
  const failed = result.failed || [];
  const attached = Number(result.imported) || 0;
  return (
    <div className="space-y-3 border-t border-slate-100 pt-4">
      <p className={clsx(
        'flex items-center gap-2 rounded-lg border px-4 py-3 text-sm',
        attached > 0 ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-slate-50 text-slate-700',
      )}>
        {attached > 0
          ? <CheckCircle size={16} className="shrink-0 text-emerald-600" aria-hidden="true" />
          : <Info size={16} className="shrink-0 text-slate-400" aria-hidden="true" />}
        <span>
          <strong className="font-semibold">{num(attached)}</strong> of {plural(result.total || 0, 'document')} attached
          {result.targetOrg ? <> to <strong className="font-semibold">{result.targetOrg}</strong></> : null}.
        </span>
      </p>

      {(skipped.length > 0 || failed.length > 0) && (
        <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <AlertTriangle size={15} className="shrink-0 text-amber-600" aria-hidden="true" />
            {[skipped.length > 0 && `${num(skipped.length)} skipped`, failed.length > 0 && `${num(failed.length)} failed`]
              .filter(Boolean).join(' · ')}. Fix the manifest and run it again.
          </p>
          {skipped.length > 0 && (
            <ul className="max-h-40 space-y-1 overflow-y-auto">
              {skipped.map((s, i) => (
                <li key={`sk-${i}`} className="flex items-start gap-2 text-[13px] text-amber-900">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
                  <span className="min-w-0 break-words"><span className="font-medium">{s.filename}</span>: {s.reason}</span>
                </li>
              ))}
            </ul>
          )}
          {failed.length > 0 && (
            <ul className="max-h-40 space-y-1 overflow-y-auto">
              {failed.map((f, i) => (
                <li key={`fl-${i}`} className="flex items-start gap-2 text-[13px] text-amber-900">
                  <XCircle size={12} className="mt-0.5 shrink-0 text-red-500" aria-hidden="true" />
                  <span className="min-w-0 break-words"><span className="font-medium">{f.filename}</span>: {f.message}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main wizard ───────────────────────────────────────────────────────────────
export default function MigrationWizard() {
  // Tenant scoping: super admins import INTO any approved org and choose it first; every other
  // admin is locked to their OWN org, never sees another tenant, and starts on the checklist.
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const confirm = useConfirm();

  // Phases: 'setup' (super admin picks the destination) → 'checklist' (the spine) → 'entity' (mini-flow)
  const [phase, setPhase] = useState(isSuperAdmin ? 'setup' : 'checklist');

  // ── Destination ──
  const [orgs,        setOrgs]        = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgError,    setOrgError]    = useState(null);     // the org list / own org failed to load
  const [targetOrg,   setTargetOrg]   = useState(null);     // { id, name, email, ... }

  // ── Entity catalogue (data-driven from the backend) ──
  const [entities,        setEntities]        = useState([]);   // [{ key,label,fields,fk,order,autoMapPlatforms }]
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [entitiesError,   setEntitiesError]   = useState(null);
  const [completed,       setCompleted]       = useState({});   // { [key]: { imported, failed, skipped, unresolved } }

  // ── Per-entity mini-flow ──
  const [activeEntity, setActiveEntity] = useState(null);       // the entity object being imported
  const [step,         setStep]         = useState(1);          // 1 upload → 2 map → 3 validate → 4 importing → 5 done
  const [file,         setFile]         = useState(null);
  const [parsedData,   setParsedData]   = useState(null);       // { columns, preview, totalRows, detectedPlatform, rows }
  const [parsing,      setParsing]      = useState(false);
  const [mappings,     setMappings]     = useState({});         // { field: csvColumn }
  const [aiMapping,    setAiMapping]    = useState(false);      // ai-map request in flight
  const [aiMapped,     setAiMapped]     = useState(false);      // mappings came from AI (or presets)
  const [mapMethod,    setMapMethod]    = useState(null);       // 'preset+ai' | 'ai' | 'preset' | null — how mappings were produced
  const [mapNotes,     setMapNotes]     = useState({});         // { field: "why this column" }
  const [unmapped,     setUnmapped]     = useState([]);         // source columns AI couldn't place
  const [suggestions,  setSuggestions]  = useState({});         // { sourceCol: { suggestion, target } } — what to do with extra data
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunning,   setDryRunning]   = useState(false);
  const [progress,     setProgress]     = useState(0);
  const [executing,    setExecuting]    = useState(false);
  const [executeResult,setExecuteResult]= useState(null);
  const [dragging,     setDragging]     = useState(false);
  const fileInputRef = useRef(null);

  // ── History ──
  const [history,        setHistory]        = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyLoaded,  setHistoryLoaded]  = useState(false);  // after the first load, a refresh keeps the rows on screen
  const [historyError,   setHistoryError]   = useState(null);
  const [undoingBatch,   setUndoingBatch]   = useState(null);   // batch_id currently being undone
  const [invitingBatch,  setInvitingBatch]  = useState(false);  // sending set-password invitations

  // ── Document Files (ZIP) uploader ──
  const [docZip,         setDocZip]         = useState(null);   // the chosen .zip File
  const [docUploading,   setDocUploading]   = useState(false);  // POST in flight
  const [docTemplating,  setDocTemplating]  = useState(false);  // template download in flight
  const [docResult,      setDocResult]      = useState(null);   // { targetOrg, total, imported, skipped, failed }
  const docInputRef = useRef(null);

  // ── Full Transfer (one multi-sheet workbook → everything) ──
  const [ftFile,        setFtFile]        = useState(null);     // the chosen .xlsx workbook
  const [ftUploading,   setFtUploading]   = useState(false);    // POST in flight
  const [ftTemplating,  setFtTemplating]  = useState(false);    // template download in flight
  const [ftResult,      setFtResult]      = useState(null);     // { targetOrg, sheets, totals, unknownSheets }
  const ftInputRef = useRef(null);

  const targetOrgId = targetOrg?.id || null;

  // Load destination + entity catalogue + history on mount.
  // Super admin → load the full org list (cross-org picker).
  // Org admin    → load ONLY their own org and lock the destination to it.
  useEffect(() => {
    if (isSuperAdmin) loadOrgs();
    else loadMyOrg();
    loadEntities();
    loadHistory();
  }, [isSuperAdmin]);

  async function loadOrgs() {
    setOrgsLoading(true);
    setOrgError(null);
    try {
      const { data } = await api.get('/superadmin/organizations', { params: { status: 'approved' } });
      const list = Array.isArray(data) ? data : (data.organizations || []);
      // Defensive: only ever offer approved orgs as import targets.
      setOrgs(list.filter(o => o.status === 'approved'));
    } catch (err) {
      setOrgError(err.response?.data?.message || "We couldn't load the organisations. Check your connection, then try again.");
    } finally {
      setOrgsLoading(false);
    }
  }

  // Org-admin path: fetch the caller's OWN org and auto-lock the destination to it.
  // The backend forces the import org regardless, so this is purely for display.
  async function loadMyOrg() {
    setOrgsLoading(true);
    setOrgError(null);
    try {
      const { data } = await api.get('/migration/my-org');
      if (data.org) setTargetOrg(data.org);
      else setOrgError(data.message || "We couldn't find your organisation, so there's nowhere to import into yet.");
    } catch (err) {
      setOrgError(err.response?.data?.message || "We couldn't load your organisation. Check your connection, then try again.");
    } finally {
      setOrgsLoading(false);
    }
  }

  async function loadEntities() {
    setEntitiesLoading(true);
    setEntitiesError(null);
    try {
      const { data } = await api.get('/migration/entities');
      const list = (data.entities || []).slice().sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
      setEntities(list);
    } catch (err) {
      setEntitiesError(err.response?.data?.message || "We couldn't load the data types you can import. Check your connection, then try again.");
    } finally {
      setEntitiesLoading(false);
    }
  }

  async function loadHistory() {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const { data } = await api.get('/migration/history');
      setHistory(data.history || []);
      setHistoryLoaded(true);
    } catch (err) {
      setHistoryError(err.response?.data?.message || "We couldn't load the import history. Check your connection, then try again.");
    } finally {
      setHistoryLoading(false);
    }
  }

  // ── Undo an import batch (own org; super admin can undo any org) ──
  // Deletes exactly the records (and files) created in that batch, FK-safe, then refreshes
  // history so the row shows "Undone" instead of the button. The warning names what will go.
  async function undoBatch(h) {
    const breakdown = recordBreakdown(h.record_counts);
    const counted = breakdown.reduce((sum, b) => sum + b.n, 0);
    const n = counted > 0 ? counted : (h.record_count || 0);
    const lead = breakdown.length ? `${joinList(breakdown.map(b => b.text))}.\n` : '';
    const ok = await confirm(
      `${lead}This permanently deletes the records this import created and cannot be undone. ` +
      'Records that newer data depends on may not be deleted.',
      { title: 'Undo this import?', confirmLabel: `Delete ${plural(n, 'imported record')}`, danger: true },
    );
    if (!ok) return;
    setUndoingBatch(h.batch_id);
    try {
      const { data } = await api.post(`/migration/undo/${h.batch_id}`);
      const failedCount = data.failed?.length || 0;
      const files = data.filesDeleted ? ` · ${plural(data.filesDeleted, 'file')} deleted` : '';
      if (failedCount === 0) {
        toast.success(`Undone: ${plural(data.deleted || 0, 'record')} removed${files}`);
      } else {
        toast.success(`${num(data.deleted || 0)} removed · ${num(failedCount)} could not be deleted${files}`);
      }
      await loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Undo failed');
    } finally {
      setUndoingBatch(null);
    }
  }

  // ── Document Files (ZIP) ──
  async function downloadDocTemplate() {
    setDocTemplating(true);
    try {
      const res = await api.get('/migration/documents/template', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'manifest.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Could not download the manifest template');
    } finally {
      setDocTemplating(false);
    }
  }

  async function importDocuments() {
    if (!targetOrgId) { toast.error('Select a destination organisation first'); return; }
    if (!docZip)      { toast.error('Choose a ZIP file to upload'); return; }
    setDocUploading(true);
    setDocResult(null);

    const formData = new FormData();
    formData.append('targetOrgId', targetOrgId);
    formData.append('file', docZip);

    try {
      const { data } = await api.post('/migration/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocResult(data);
      const skipped = data.skipped?.length || 0;
      const failed  = data.failed?.length || 0;
      if (skipped === 0 && failed === 0) {
        toast.success(`${plural(data.imported || 0, 'document')} attached`);
      } else {
        toast.success(`${num(data.imported || 0)} attached · ${num(skipped + failed)} need attention`);
      }
      await loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Document import failed');
    } finally {
      setDocUploading(false);
    }
  }

  // ── Full Transfer (one workbook → everything) ──
  async function downloadFullTransferTemplate() {
    setFtTemplating(true);
    try {
      const res = await api.get('/migration/full-transfer/template', { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'rostery_full_transfer_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Could not download the import template');
    } finally {
      setFtTemplating(false);
    }
  }

  async function runFullTransfer() {
    if (!targetOrgId) { toast.error('Select a destination organisation first'); return; }
    if (!ftFile)      { toast.error('Choose a workbook (.xlsx) to upload'); return; }
    setFtUploading(true);
    setFtResult(null);

    const formData = new FormData();
    formData.append('targetOrgId', targetOrgId);
    formData.append('file', ftFile);

    try {
      const { data } = await api.post('/migration/full-transfer', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFtResult(data);
      // Reflect imported sheets on the per-entity checklist too.
      setCompleted(c => {
        const next = { ...c };
        for (const s of (data.sheets || [])) {
          next[s.entity] = {
            imported:   s.imported   || 0,
            failed:     s.failed     || 0,
            skipped:    s.skipped    || 0,
            unresolved: s.unresolved || 0,
          };
        }
        return next;
      });
      const t = data.totals || {};
      toast.success(`${plural(t.imported || 0, 'record')} imported across ${plural(data.sheets?.length || 0, 'sheet')}`);
      await loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || 'The workbook import failed');
    } finally {
      setFtUploading(false);
    }
  }

  // Entities grouped by `order` for the checklist spine.
  const groupedEntities = useMemo(() => {
    const map = new Map();
    for (const e of entities) {
      if (!map.has(e.order)) map.set(e.order, []);
      map.get(e.order).push(e);
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [entities]);

  // Data type key → display name, for the history table.
  const entityNames = useMemo(
    () => Object.fromEntries(entities.map(e => [e.key, sentenceCase(e.label)])),
    [entities],
  );

  // ── Reset everything (super admin: change org / start over) ──
  function fullReset() {
    setPhase(isSuperAdmin ? 'setup' : 'checklist');
    if (isSuperAdmin) setTargetOrg(null);   // an org admin's destination is fixed
    setCompleted({});
    setFtFile(null);
    setFtResult(null);
    setDocZip(null);
    setDocResult(null);
    clearEntityFlow();
  }

  function clearEntityFlow() {
    setActiveEntity(null);
    setStep(1);
    setFile(null);
    setParsedData(null);
    setMappings({});
    setAiMapping(false);
    setAiMapped(false);
    setMapMethod(null);
    setMapNotes({});
    setUnmapped([]);
    setSuggestions({});
    setDryRunResult(null);
    setExecuteResult(null);
    setProgress(0);
    setDragging(false);
  }

  // ── Begin importing a specific entity (from the checklist) ──
  function startEntity(entity) {
    clearEntityFlow();
    setActiveEntity(entity);
    setStep(1);
    setPhase('entity');
  }

  function backToChecklist() {
    clearEntityFlow();
    setPhase('checklist');
  }

  // ── File handling ──
  async function handleFile(f) {
    if (!f || !activeEntity) return;
    setFile(f);
    setParsedData(null);
    setMappings({});
    setAiMapped(false);
    setMapMethod(null);
    setMapNotes({});
    setUnmapped([]);
    setSuggestions({});
    setDryRunResult(null);
    setParsing(true);

    const formData = new FormData();
    formData.append('file', f);

    try {
      const { data } = await api.post('/migration/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setParsedData(data);

      // ── Mapping: PRESETS first (instant, free), then AI fills the gaps ──
      // 1) Run the deterministic synonym presets for the detected platform + entity.
      //    This is free (no API) and resolves most known-format exports immediately.
      const platform = data.detectedPlatform || SOURCE_PLATFORM;
      let presetMappings = {};
      try {
        const { data: autoData } = await api.post('/migration/auto-map', {
          columns:    data.columns,
          platform,
          entityType: activeEntity.key,
        });
        presetMappings = autoData.mappings || {};
      } catch { /* presets unavailable — AI will carry the mapping */ }

      // Show presets right away so the user isn't staring at an empty grid while AI runs.
      setMappings(presetMappings);

      // 2) Ask the AI to map ONLY the columns the presets didn't already use, so it
      //    fills the gaps without fighting the presets. Merge so PRESET WINS; AI fills
      //    the rest; AI's unmapped + suggestions still surface in "Extra data found".
      const presetCols = new Set(Object.values(presetMappings));
      const gapColumns = data.columns.filter(c => !presetCols.has(c));
      const presetFieldCount = Object.keys(presetMappings).length;

      setAiMapping(true);
      try {
        const { data: aiData } = await api.post('/migration/ai-map', {
          entityType: activeEntity.key,
          columns:    gapColumns.length ? gapColumns : data.columns,
          preview:    data.preview,
        });

        // Merge: start from presets, then add AI mappings for fields presets left empty.
        const merged = { ...presetMappings };
        for (const [field, col] of Object.entries(aiData.mappings || {})) {
          if (!merged[field] && col) merged[field] = col;       // preset wins per field
        }
        setMappings(merged);
        setMapNotes(aiData.notes || {});

        // Recompute "extra data" against the FINAL merged mapping so any column the
        // presets consumed isn't wrongly flagged as unmapped.
        const usedCols = new Set(Object.values(merged));
        const finalUnmapped = data.columns.filter(c => !usedCols.has(c));
        setUnmapped(finalUnmapped);

        // Keep only suggestions that still apply to a genuinely-unmapped column.
        const sug = {};
        for (const col of finalUnmapped) {
          if (aiData.suggestions && aiData.suggestions[col]) sug[col] = aiData.suggestions[col];
        }
        setSuggestions(sug);

        const aiAvailable = aiData.ai !== false;
        setAiMapped(Object.keys(merged).length > 0);
        setMapMethod(presetFieldCount > 0 ? (aiAvailable ? 'preset+ai' : 'preset') : (aiAvailable ? 'ai' : 'preset'));
      } catch {
        // AI unavailable — keep the preset mappings and flag remaining columns as extra.
        const usedCols = new Set(Object.values(presetMappings));
        setUnmapped(data.columns.filter(c => !usedCols.has(c)));
        setSuggestions({});
        setAiMapped(presetFieldCount > 0);
        setMapMethod(presetFieldCount > 0 ? 'preset' : null);
      } finally {
        setAiMapping(false);
      }

      toast.success(`Read ${plural(data.totalRows, 'row')} from ${f.name}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to parse file');
      setFile(null);
    } finally {
      setParsing(false);
    }
  }

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  }, [activeEntity]);

  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  // ── Dry run ──
  async function runDryRun() {
    if (!parsedData || !activeEntity || !targetOrgId) return;
    setDryRunning(true);
    setDryRunResult(null);
    try {
      const { data } = await api.post('/migration/dry-run', {
        targetOrgId,
        entityType: activeEntity.key,
        mappings,
        rows: parsedData.rows,
        sourcePlatform: SOURCE_PLATFORM,
      });
      setDryRunResult(data);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Validation failed');
    } finally {
      setDryRunning(false);
    }
  }

  // ── Execute ──
  async function runExecute() {
    if (!parsedData || !activeEntity || !targetOrgId) return;
    setStep(4);
    setExecuting(true);
    setProgress(0);

    const interval = setInterval(() => {
      setProgress(p => Math.min(p + Math.random() * 12, 85));
    }, 300);

    try {
      const { data } = await api.post('/migration/execute', {
        targetOrgId,
        entityType: activeEntity.key,
        mappings,
        rows: parsedData.rows,
        sourcePlatform: SOURCE_PLATFORM,
      });
      clearInterval(interval);
      setProgress(100);
      setExecuteResult(data);
      // Mark this entity done on the checklist with its counts.
      setCompleted(c => ({
        ...c,
        [activeEntity.key]: {
          imported:   data.imported   || 0,
          failed:     data.failed     || 0,
          skipped:    data.skipped    || 0,
          unresolved: data.unresolved || 0,
        },
      }));
      await loadHistory();
      setTimeout(() => setStep(5), 600);
    } catch (err) {
      clearInterval(interval);
      toast.error(err.response?.data?.message || 'Import failed');
      setStep(3);
    } finally {
      setExecuting(false);
    }
  }

  const activeFields = activeEntity?.fields || [];
  const hasFk = activeEntity?.fk?.length > 0;
  const entityInline = activeEntity ? inlineLabel(activeEntity.label) : '';
  const fieldLabel = (key) => sentenceCase(activeFields.find(f => f.key === key)?.label || humanise(key));
  const fkWords = (activeEntity?.fk || []).map(k => FK_NOUN[k] || k).join(' or ');
  const parentsFirst = [
    activeEntity?.fk?.includes('client') && 'clients',
    activeEntity?.fk?.includes('staff') && 'staff',
  ].filter(Boolean).join(' and ') || (activeEntity?.fk || []).join(' and ');

  // Friendly "Detected source" line for the mapping step. Presets are best-effort
  // header guesses and the AI mapper is the safety net — so the wording never claims
  // an exact source, it reflects HOW the mappings were produced.
  const detection   = parsedData?.detection || null;
  const detectedLbl = detection?.label;
  const strongDetect = detection && detection.platform !== 'generic' && (detection.confidence || 0) >= 0.4;
  const mapSourceLine = (() => {
    if (mapMethod === 'preset+ai') {
      return strongDetect
        ? `Detected source: ${detectedLbl} · matched by presets and AI`
        : 'Matched by presets and AI';
    }
    if (mapMethod === 'preset') {
      return strongDetect
        ? `Detected source: ${detectedLbl} · matched by presets`
        : 'Matched by presets';
    }
    if (mapMethod === 'ai') return 'Matched by AI';
    return null;
  })();

  // A super admin with no destination is always on the picker, whatever the phase says.
  const view = isSuperAdmin && !targetOrg ? 'setup' : phase;

  const historyColumns = [
    { key: 'created_at', header: 'Date', render: (h) => <span className="whitespace-nowrap">{fmtDateTime(h.created_at)}</span> },
    { key: 'entity_type', header: 'Data type', render: (h) => entityNames[h.entity_type] || humanise(h.entity_type) || '—' },
    { key: 'source_platform', header: 'Source', render: (h) => sourceLabel(h.source_platform) },
    { key: 'total_records', header: 'Rows', align: 'right', className: 'tabular-nums', render: (h) => num(h.total_records) },
    { key: 'imported', header: 'Imported', align: 'right', className: 'tabular-nums', render: (h) => num(h.imported) },
    {
      key: 'failed', header: 'Failed', align: 'right', className: 'tabular-nums',
      render: (h) => <span className={h.failed > 0 ? 'font-semibold text-red-600' : undefined}>{num(h.failed)}</span>,
    },
    { key: 'status', header: 'Status', render: (h) => <StatusPill status={h.status} map={HISTORY_STATUS} /> },
    { key: 'created_by_name', header: 'By', render: (h) => <span className="whitespace-nowrap">{h.created_by_name || '—'}</span> },
    {
      key: 'undo', header: 'Undo', align: 'right',
      render: (h) => {
        if (h.undone_at) return <StatusPill status="undone" label="Undone" tone="neutral" />;
        if (!(h.batch_id && (h.record_count || 0) > 0)) {
          return <span className="text-slate-400"><span aria-hidden="true">—</span><span className="sr-only">Nothing to undo</span></span>;
        }
        const busy = undoingBatch === h.batch_id;
        const what = entityNames[h.entity_type] || humanise(h.entity_type) || 'data';
        return (
          <button
            type="button"
            onClick={() => undoBatch(h)}
            disabled={busy}
            className="btn-secondary btn-sm"
            aria-label={`Undo the ${what} import from ${fmtDateTime(h.created_at)}`}
            title={`Delete the ${plural(h.record_count, 'record')} imported in this batch`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : <RotateCcw size={12} aria-hidden="true" />}
            {busy ? 'Undoing…' : 'Undo'}
          </button>
        );
      },
    },
  ];

  return (
    <div className={PAGE}>

      {/* Where the data is going, said once and quietly. */}
      {view !== 'setup' && (
        targetOrg ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="text-sm text-slate-600">
                Importing into <span className="font-semibold text-slate-900">{targetOrg.name}</span>
              </p>
              {isSuperAdmin && (
                <button type="button" onClick={fullReset} className="btn-ghost btn-sm">
                  Change organisation
                </button>
              )}
            </div>
            {view === 'checklist' && <AiMappingNote />}
          </div>
        ) : orgError ? (
          <div className="rounded-xl border border-slate-200 bg-white">
            <ErrorState message={orgError} onRetry={loadMyOrg} />
          </div>
        ) : (
          <div className="space-y-2" aria-busy="true" aria-label="Loading your organisation">
            <div className="skeleton h-5 w-64 max-w-full" />
            <div className="skeleton h-4 w-full max-w-xl" />
          </div>
        )
      )}

      {/* ════════════════ SETUP: destination org (super admin only) ════════════════ */}
      {view === 'setup' && (
        <SectionCard title="Choose the organisation to import into" icon={Building2} bodyClass="space-y-5 p-5">
          <p className="text-sm text-slate-500">
            All imported data lands in this organisation. Only approved organisations can receive an import.
          </p>
          {orgError ? (
            <div className="rounded-xl border border-slate-200">
              <ErrorState message={orgError} onRetry={loadOrgs} />
            </div>
          ) : (
            <div className="max-w-xl">
              <OrgPicker orgs={orgs} loading={orgsLoading} value={targetOrgId} onChange={setTargetOrg} />
            </div>
          )}
          <AiMappingNote />
          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-4">
            {!targetOrgId && <p className="text-sm text-slate-500">Choose an organisation to continue.</p>}
            <button type="button" className="btn-primary" disabled={!targetOrgId} onClick={() => setPhase('checklist')}>
              Continue <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </SectionCard>
      )}

      {/* ════════════════ CHECKLIST (the spine) ════════════════ */}
      {view === 'checklist' && !targetOrgId && !orgError && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" aria-hidden="true">
          {[0, 1, 2, 3].map(i => <div key={i} className="skeleton h-24 rounded-xl" />)}
        </div>
      )}

      {view === 'checklist' && targetOrgId && (
        <>
          {/* ── Everything in one workbook, in dependency order ── */}
          <SectionCard title="Import everything in one file" icon={FileSpreadsheet} bodyClass="space-y-4 p-5">
            <p className="text-sm leading-relaxed text-slate-600">
              Upload one Excel workbook with a sheet for each data type, and Rostery imports every sheet in the
              right order: clients and staff first, then everything that links to them. Rows link to clients by
              NDIS number or name, and to staff by email or name.
            </p>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <button
                type="button"
                onClick={downloadFullTransferTemplate}
                disabled={ftTemplating}
                className="btn-secondary btn-sm self-start sm:self-auto"
              >
                {ftTemplating ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Download size={13} aria-hidden="true" />}
                {ftTemplating ? 'Preparing…' : 'Download template'}
              </button>
              <p className="text-sm text-slate-500">
                It has a sheet for each data type with the right column headers, including the link columns.
                Fill the sheets you have and leave the rest blank.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <FilePickButton
                file={ftFile}
                icon={FileSpreadsheet}
                disabled={ftUploading}
                onPick={() => ftInputRef.current?.click()}
                emptyTitle="Choose a workbook (.xlsx)"
                emptyHint="One sheet per data type. Empty sheets are skipped."
                changeHint="Choose a different workbook"
              />
              <input
                ref={ftInputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={e => { setFtFile(e.target.files?.[0] || null); setFtResult(null); }}
              />
              <button
                type="button"
                onClick={runFullTransfer}
                disabled={ftUploading || !ftFile || !targetOrgId}
                className="btn-primary shrink-0"
              >
                {ftUploading ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Upload size={15} aria-hidden="true" />}
                {ftUploading ? 'Importing everything…' : 'Import everything'}
              </button>
            </div>

            {ftResult && <FullTransferReport result={ftResult} />}
          </SectionCard>

          {/* ── One data type at a time ── */}
          <SectionCard
            title="Import one type at a time"
            icon={ListChecks}
            actions={entities.length > 0 && (
              <span className="text-xs tabular-nums text-slate-500">
                {num(Object.keys(completed).length)} of {num(entities.length)} imported
              </span>
            )}
            bodyClass="space-y-5 p-5"
          >
            <p className="text-sm leading-relaxed text-slate-600">
              Import in this order so links resolve: clients and staff first, then the funding, shifts, notes
              and incidents that point back to them. If a dependent type goes in first, rows whose client or
              staff member doesn't exist yet are skipped.
            </p>

            {entitiesLoading ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" aria-busy="true" aria-label="Loading data types">
                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-[74px] rounded-xl" />)}
              </div>
            ) : entitiesError ? (
              <div className="rounded-xl border border-slate-200">
                <ErrorState message={entitiesError} onRetry={loadEntities} />
              </div>
            ) : (
              <ol className="space-y-5">
                {groupedEntities.map(([order, group]) => {
                  const OrderIcon = ORDER_ICON[order] || Database;
                  return (
                    <li key={order}>
                      <div className="mb-2 flex items-center gap-2">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold tabular-nums text-slate-600">
                          {order}
                        </span>
                        <OrderIcon size={15} className="shrink-0 text-slate-400" aria-hidden="true" />
                        <p className="text-sm font-medium text-slate-700">{ORDER_NOTE[order] || `Step ${order}`}</p>
                      </div>
                      <ul className="grid grid-cols-1 gap-3 sm:ml-8 sm:grid-cols-2">
                        {group.map(entity => {
                          const done = completed[entity.key];
                          const links = entity.fk?.length ? entity.fk.map(k => FK_NOUN[k] || k).join(' and ') : null;
                          return (
                            <li key={entity.key} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
                              <div className="min-w-0">
                                <div className="flex min-w-0 items-center gap-2">
                                  {done
                                    ? <CheckCircle size={16} className="shrink-0 text-emerald-600" aria-hidden="true" />
                                    : <span className="h-4 w-4 shrink-0 rounded-full border-2 border-slate-300" aria-hidden="true" />}
                                  <p className="truncate text-sm font-semibold text-slate-900">
                                    {sentenceCase(entity.label)}
                                    {done && <span className="sr-only"> (imported)</span>}
                                  </p>
                                </div>
                                {done ? (
                                  <p className="ml-6 mt-1 text-xs text-slate-600">
                                    <span className="font-medium text-emerald-700">{num(done.imported)} imported</span>
                                    {done.skipped > 0 && <span> · {num(done.skipped)} skipped</span>}
                                    {done.unresolved > 0 && <span className="text-amber-700"> · {num(done.unresolved)} unlinked</span>}
                                    {done.failed > 0 && <span className="text-red-600"> · {num(done.failed)} failed</span>}
                                  </p>
                                ) : (
                                  <p className="ml-6 mt-1 text-xs text-slate-500">
                                    {plural(entity.fields.length, 'field')}
                                    {links && (
                                      <> · <Link2 size={11} className="-mt-px inline" aria-hidden="true" /> links to {links}</>
                                    )}
                                  </p>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => startEntity(entity)}
                                className="btn-secondary btn-sm shrink-0"
                                aria-label={`${done ? 'Re-import' : 'Import'} ${inlineLabel(entity.label)}`}
                              >
                                {done ? <RefreshCw size={12} aria-hidden="true" /> : <Upload size={12} aria-hidden="true" />}
                                {done ? 'Re-import' : 'Import'}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ol>
            )}
          </SectionCard>

          {/* ── Document Files (ZIP): files, not spreadsheet rows ── */}
          <SectionCard title="Document files (ZIP)" icon={Paperclip} bodyClass="space-y-4 p-5">
            <p className="text-sm leading-relaxed text-slate-600">
              Bring across the files themselves, such as police checks, signed agreements and plan PDFs. Put them
              in a ZIP with a{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5 text-xs text-slate-700">manifest.csv</code>{' '}
              that says which client or staff member each file belongs to.
            </p>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <button
                type="button"
                onClick={downloadDocTemplate}
                disabled={docTemplating}
                className="btn-secondary btn-sm self-start sm:self-auto"
              >
                {docTemplating ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Download size={13} aria-hidden="true" />}
                {docTemplating ? 'Preparing…' : 'Download manifest template'}
              </button>
              <p className="text-sm text-slate-500">Fill in one row per file, then zip the manifest with your documents.</p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <FilePickButton
                file={docZip}
                icon={Paperclip}
                disabled={docUploading}
                onPick={() => docInputRef.current?.click()}
                emptyTitle="Choose a ZIP file"
                emptyHint="A .zip of your files and manifest.csv"
                changeHint="Choose a different ZIP"
              />
              <input
                ref={docInputRef}
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                className="hidden"
                onChange={e => { setDocZip(e.target.files?.[0] || null); setDocResult(null); }}
              />
              <button
                type="button"
                onClick={importDocuments}
                disabled={docUploading || !docZip || !targetOrgId}
                className="btn-primary shrink-0"
              >
                {docUploading ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Upload size={15} aria-hidden="true" />}
                {docUploading ? 'Uploading…' : 'Import documents'}
              </button>
            </div>

            {docResult && <DocumentReport result={docResult} />}
          </SectionCard>
        </>
      )}

      {/* ════════════════ ENTITY mini-flow ════════════════ */}
      {view === 'entity' && activeEntity && (
        <SectionCard
          title={`Import ${entityInline}`}
          icon={FileSpreadsheet}
          actions={(
            <button type="button" onClick={backToChecklist} className="btn-ghost btn-sm shrink-0">
              <ListChecks size={13} aria-hidden="true" /> Back to checklist
            </button>
          )}
          bodyClass="p-5 sm:p-6"
        >
          <StepBar current={step} />

          {/* ── STEP 1: Upload file ── */}
          {step === 1 && (
            <div className="space-y-5">
              {/* Drop target. The buttons inside are the keyboard route; a click anywhere also opens the picker. */}
              <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={() => !parsedData && !parsing && fileInputRef.current?.click()}
                className={clsx(
                  'rounded-xl border border-dashed p-8 text-center transition-colors duration-150 sm:p-10',
                  dragging
                    ? 'border-primary-400 bg-primary-50'
                    : parsedData
                      ? 'border-slate-300 bg-slate-50'
                      : 'cursor-pointer border-slate-300 hover:border-slate-400 hover:bg-slate-50',
                )}
              >
                {parsing ? (
                  <p className="inline-flex items-center gap-2 text-sm font-medium text-slate-600" role="status">
                    <Loader2 size={16} className="animate-spin text-slate-400" aria-hidden="true" />
                    Reading your file…
                  </p>
                ) : parsedData ? (
                  <div className="flex flex-col items-center gap-2">
                    <CheckCircle size={24} className="text-emerald-600" aria-hidden="true" />
                    <p className="font-semibold text-slate-900">
                      {plural(parsedData.totalRows, 'row')} found in <span className="break-all font-medium text-slate-700">{file?.name}</span>
                    </p>
                    <p className="text-sm text-slate-500">{plural(parsedData.columns.length, 'column')} detected</p>
                    {strongDetect && (
                      <p className="inline-flex items-center gap-1.5 text-sm text-slate-500">
                        <Wand2 size={13} className="shrink-0 text-slate-400" aria-hidden="true" />
                        Detected source: {detectedLbl}. Presets map the columns they know and AI fills the rest.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                      className="btn-secondary btn-sm mt-1"
                    >
                      Choose a different file
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <Upload size={28} className="text-slate-300" aria-hidden="true" />
                    <div>
                      <p className="font-semibold text-slate-700">Drop the {entityInline} export here</p>
                      <p className="mt-1 text-sm text-slate-500">.csv and .xlsx files are accepted</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                      className="btn-secondary btn-sm"
                    >
                      Choose a file
                    </button>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={e => handleFile(e.target.files?.[0])}
              />

              {/* Column preview */}
              {parsedData && (
                <div>
                  <p className="mb-2 text-sm font-medium text-slate-700">
                    {parsedData.preview.length === 1 ? 'Preview of the first row' : `Preview of the first ${parsedData.preview.length} rows`}
                  </p>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="border-b border-slate-200 bg-white">
                        <tr>
                          {parsedData.columns.slice(0, 8).map(c => (
                            <th key={c} scope="col" className="whitespace-nowrap px-3 py-2 text-left font-semibold text-slate-600">{c}</th>
                          ))}
                          {parsedData.columns.length > 8 && (
                            <th scope="col" className="whitespace-nowrap px-3 py-2 text-left font-normal text-slate-500">
                              +{parsedData.columns.length - 8} more
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {parsedData.preview.map((row, i) => (
                          <tr key={i}>
                            {parsedData.columns.slice(0, 8).map(c => (
                              <td key={c} className="max-w-[160px] truncate whitespace-nowrap px-3 py-2 text-slate-700">
                                {String(row[c] ?? '')}
                              </td>
                            ))}
                            {parsedData.columns.length > 8 && <td />}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* AI Analysis panel */}
              {parsedData && (
                <AiAnalysisPanel
                  platform={SOURCE_PLATFORM}
                  entityType={activeEntity.key}
                  entityLabel={entityInline}
                  parsedData={parsedData}
                  mappings={mappings}
                />
              )}

              <div className="flex items-center justify-between gap-3">
                <button type="button" onClick={backToChecklist} className="btn-secondary">
                  <ChevronLeft size={15} aria-hidden="true" /> Back
                </button>
                <button type="button" disabled={!parsedData} onClick={() => setStep(2)} className="btn-primary">
                  Map fields <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 2: Map fields ── */}
          {step === 2 && parsedData && (
            <div className="space-y-5">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Map fields</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Choose the column in your file that fills each Rostery field. Fields marked{' '}
                  <span className="font-semibold text-rose-600">*</span> are required.
                  {hasFk && (
                    <> Link fields (marked <Link2 size={12} className="-mt-px inline text-slate-400" aria-hidden="true" /><span className="sr-only">with a link icon</span>) are
                    matched to an existing {fkWords} in {targetOrg?.name}.</>
                  )}
                </p>
              </div>

              {/* AI mapping in flight */}
              {aiMapping && (
                <p className="flex items-center gap-2 text-sm text-slate-500" role="status">
                  <Loader2 size={14} className="animate-spin text-slate-400" aria-hidden="true" />
                  Presets applied. AI is filling any remaining gaps…
                </p>
              )}

              {/* How the matches were made — presets + AI (or whichever produced them) */}
              {!aiMapping && aiMapped && (
                <p className="flex items-start gap-2 text-sm leading-relaxed text-slate-500">
                  <Wand2 size={15} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
                  <span>
                    <span className="font-medium text-slate-700">{mapSourceLine || 'Matched automatically'}.</span>{' '}
                    These are best-effort matches from your column headers and sample values. Check each one and
                    change anything that doesn't look right before importing.
                  </span>
                </p>
              )}

              <div className="overflow-hidden rounded-xl border border-slate-200">
                <div className="hidden grid-cols-2 gap-4 border-b border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 sm:grid">
                  <span>Rostery field</span>
                  <span>Column in your file</span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {activeFields.map(f => {
                    const id = `map-${f.key}`;
                    const isLink = /^(client_|staff_)/.test(f.key);
                    const value = mappings[f.key] || '';
                    const isMapped = !!value && value !== '__skip__';
                    const missing = f.required && !isMapped;
                    return (
                      <li key={f.key} className="grid grid-cols-1 gap-1.5 px-4 py-3 sm:grid-cols-2 sm:items-start sm:gap-4">
                        <label htmlFor={id} className="flex items-center gap-1.5 text-sm font-medium text-slate-700 sm:pt-2">
                          {isLink && <Link2 size={12} className="shrink-0 text-slate-400" aria-hidden="true" />}
                          {sentenceCase(f.label)}
                          {f.required && <span className="font-semibold text-rose-600" aria-hidden="true">*</span>}
                          {f.required && <span className="sr-only">(required)</span>}
                        </label>
                        <div className="min-w-0">
                          <select
                            id={id}
                            value={value}
                            onChange={e => setMappings(m => ({ ...m, [f.key]: e.target.value }))}
                            className={clsx('input-field', isMapped ? 'text-slate-900' : 'text-slate-500', missing && 'border-amber-300')}
                          >
                            <option value="">Choose a column</option>
                            <option value="__skip__">Skip this field</option>
                            {parsedData.columns.map(col => (
                              <option key={col} value={col}>{col}</option>
                            ))}
                          </select>
                          {mapNotes[f.key] && isMapped && (
                            <p className="mt-1 flex items-start gap-1 text-xs leading-snug text-slate-500">
                              <Sparkles size={11} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
                              {mapNotes[f.key]}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Extra data found — source columns nothing was matched to, with a
                  per-column suggestion for what to do with that data. */}
              {unmapped.length > 0 && (
                <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <Info size={16} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-amber-900">Extra data found</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-amber-800">
                      These columns weren't matched to a Rostery field, so their data won't be imported unless you
                      act on it. Map a column above if one fits, or follow the suggestion:
                    </p>
                    <ul className="mt-2 space-y-1">
                      {unmapped.map((col) => {
                        const sug = suggestions[col];
                        return (
                          <li key={col} className="break-words text-[13px] leading-relaxed text-amber-900">
                            <span className="font-medium">{col}</span>
                            <span className="text-amber-800"> → {sug?.suggestion || 'No matching field; consider a custom field'}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between gap-3">
                <button type="button" onClick={() => setStep(1)} className="btn-secondary">
                  <ChevronLeft size={15} aria-hidden="true" /> Back
                </button>
                <button type="button" disabled={dryRunning} onClick={runDryRun} className="btn-primary">
                  {dryRunning
                    ? <><Loader2 size={15} className="animate-spin" aria-hidden="true" /> Validating…</>
                    : <><CheckCircle size={15} aria-hidden="true" /> Run validation preview</>}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: Validate (dry run) ── */}
          {step === 3 && dryRunResult && (
            <div className="space-y-5">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Validation results</h2>
                <p className="mt-1 text-sm text-slate-500">Nothing has been imported yet. Review any issues, then import the valid rows.</p>
              </div>

              <ResultTiles
                imported={dryRunResult.valid}
                skipped={dryRunResult.skipped}
                unresolved={dryRunResult.unresolved || 0}
                failed={dryRunResult.invalid}
                showUnlinked={hasFk}
                importedLabel="Will import"
                skippedLabel="Duplicates skipped"
                unlinkedLabel="Unlinked (skipped)"
                failedLabel="Invalid (skipped)"
              />

              {/* Unresolved-link guidance */}
              {dryRunResult.unresolved > 0 && (
                <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <Link2 size={16} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-amber-900">
                      {plural(dryRunResult.unresolved, 'row')} couldn't be linked to an existing {fkWords} in {targetOrg?.name}.
                    </p>
                    <p className="mt-1 text-sm text-amber-800">
                      These rows will be skipped. Import {parentsFirst} first, then run this import again so the links resolve.
                    </p>
                  </div>
                </div>
              )}

              {/* Preview rows */}
              {dryRunResult.preview?.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-medium text-slate-700">
                    {dryRunResult.preview.length === 1 ? 'Preview of the first valid row' : `Preview of the first ${dryRunResult.preview.length} valid rows`}
                  </p>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="border-b border-slate-200 bg-white">
                        <tr>
                          {Object.keys(dryRunResult.preview[0]).map(k => (
                            <th key={k} scope="col" className="whitespace-nowrap px-3 py-2 text-left font-semibold text-slate-600">{fieldLabel(k)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {dryRunResult.preview.map((row, i) => (
                          <tr key={i}>
                            {Object.values(row).map((v, j) => (
                              <td key={j} className="max-w-[160px] truncate whitespace-nowrap px-3 py-2 text-slate-700">
                                {String(v ?? '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Row issues */}
              <IssueList
                title={`Issues (${num(dryRunResult.errors?.length || 0)})`}
                icon={AlertTriangle}
                items={(dryRunResult.errors || []).map((e, i) => ({
                  key: i,
                  text: <>Row {e.row}: {e.field && <><strong className="font-semibold">{fieldLabel(e.field)}</strong> — </>}{e.message}</>,
                }))}
              />

              {dryRunResult.valid === 0 && (
                <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <AlertTriangle size={16} className="shrink-0 text-amber-600" aria-hidden="true" />
                  <p className="text-sm font-medium text-amber-900">
                    No valid records to import.
                    {dryRunResult.unresolved > 0
                      ? ' Import the linked types first, then come back.'
                      : ' Go back and fix your field mappings.'}
                  </p>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <button type="button" onClick={() => setStep(2)} className="btn-secondary">
                  <ChevronLeft size={15} aria-hidden="true" /> Back to mapping
                </button>
                <button type="button" disabled={dryRunResult.valid === 0} onClick={runExecute} className="btn-primary">
                  <ArrowRight size={15} aria-hidden="true" />
                  Import {plural(dryRunResult.valid, 'record')}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 4: Importing ── */}
          {step === 4 && (
            <div className="mx-auto max-w-md space-y-4 py-8 text-center" role="status" aria-live="polite">
              <div>
                <p className="text-lg font-semibold text-slate-900">
                  Importing {num(dryRunResult?.valid || parsedData?.totalRows || 0)} {entityInline}…
                </p>
                <p className="mt-1 text-sm text-slate-500">Creating records in {targetOrg?.name}.</p>
              </div>
              <ProgressBar pct={progress} />
              <p className="text-xs tabular-nums text-slate-500">{Math.round(progress)}%</p>
            </div>
          )}

          {/* ── STEP 5: Done ── */}
          {step === 5 && executeResult && (
            <div className="space-y-5 py-2">
              <div className="flex flex-col items-center gap-2 text-center">
                {executeResult.imported > 0
                  ? <CheckCircle size={28} className="text-emerald-600" aria-hidden="true" />
                  : <AlertTriangle size={28} className="text-amber-500" aria-hidden="true" />}
                <h2 className="text-lg font-semibold text-slate-900">
                  {executeResult.imported > 0 ? `${sentenceCase(activeEntity.label)} imported` : `No ${entityInline} were imported`}
                </h2>
                <p className="text-sm text-slate-500">
                  {executeResult.imported > 0
                    ? `Loaded into ${targetOrg?.name}.`
                    : 'Check the issues below, fix the file and try again.'}
                </p>
              </div>

              <div className={clsx('mx-auto w-full', hasFk ? 'max-w-2xl' : 'max-w-xl')}>
                <ResultTiles
                  imported={executeResult.imported}
                  skipped={executeResult.skipped}
                  unresolved={executeResult.unresolved || 0}
                  failed={executeResult.failed}
                  showUnlinked={hasFk}
                />
              </div>

              {executeResult.errors?.length > 0 && (
                <div className="mx-auto w-full max-w-2xl">
                  <IssueList
                    title="Issues during import"
                    maxHeight="max-h-32"
                    items={executeResult.errors.map((e, i) => ({ key: i, text: `Row ${e.row}: ${e.message}` }))}
                  />
                </div>
              )}

              {activeEntity.key === 'staff' && executeResult.imported > 0 && (
                <div className="mx-auto max-w-md rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center text-sm text-amber-900 space-y-2">
                  <p>Imported staff have no password yet. Each one sets their own from an invitation link (valid 72 hours), or from Staff, Invite to app.</p>
                  {executeResult.batchId && (
                    <button type="button" className="btn-secondary btn-sm" disabled={invitingBatch}
                      onClick={async () => {
                        setInvitingBatch(true);
                        try {
                          const { data } = await api.post(`/migration/invite-staff/${executeResult.batchId}`);
                          toast.success(`Invitations sent to ${data.sent} of ${data.invited} staff`);
                        } catch (e) { toast.error(e.response?.data?.message || 'Could not send the invitations'); }
                        finally { setInvitingBatch(false); }
                      }}>
                      {invitingBatch ? 'Sending' : 'Email them each an invitation'}
                    </button>
                  )}
                </div>
              )}

              <div className="flex flex-wrap justify-center gap-3">
                <button type="button" onClick={backToChecklist} className="btn-primary">
                  <ListChecks size={15} aria-hidden="true" />
                  Back to checklist
                </button>
                <Link to={activeEntity.key === 'staff' ? '/staff' : '/clients'} className="btn-secondary">
                  <Users size={15} aria-hidden="true" />
                  {activeEntity.key === 'staff' ? 'View staff' : 'View clients'}
                </Link>
              </div>
            </div>
          )}
        </SectionCard>
      )}

      {/* ── Import history, with a per-batch Undo ── */}
      <div id="migration-history">
        <SectionCard
          title="Import history"
          icon={History}
          actions={(
            <button type="button" onClick={loadHistory} disabled={historyLoading} className="btn-ghost btn-sm">
              <RefreshCw size={12} className={clsx(historyLoading && 'animate-spin')} aria-hidden="true" />
              Refresh
            </button>
          )}
        >
          <DataTable
            label="Import history"
            columns={historyColumns}
            rows={history.slice(0, 8)}
            rowKey="id"
            loading={historyLoading && !historyLoaded}
            error={historyError}
            onRetry={loadHistory}
            stickyHeader={false}
            empty={{ icon: History, title: 'No imports yet', hint: 'Each import appears here with an Undo option.' }}
          />
        </SectionCard>
      </div>
    </div>
  );
}
